import * as React from 'react'; // https://github.com/parcel-bundler/parcel/issues/1199
import * as propTypes from 'prop-types';
import { FormattedHTMLMessage } from 'react-intl';
import Downshift from 'downshift';
import { FormSelectChoice, InputSelectOnKeyDown, FieldId, ExtendedFieldId } from '../../../lib/types/types';
import { InputSelectListComponent } from './InputSelectList/InputSelectListComponent';
import { InputSelectButtonComponent } from './InputSelectButton/InputSelectButtonComponent';
import { setRef } from '../../../lib/refs/refs';


interface InputSelectProps {
    onChange: any;
    options: FormSelectChoice[];
    fieldId: FieldId | ExtendedFieldId;
    className?: string;
    disabled?: boolean;
    inputId?: string;
    isErrored?: boolean;
    onKeyDown?: InputSelectOnKeyDown;
    placeholder?: string;
    value?: FormSelectChoice;
    autoFocus?: boolean;
}

const InputSelect = ({
    className,
    inputId,
    disabled,
    fieldId,
    isErrored,
    options,
    onKeyDown,
    onChange,
    placeholder,
    value,
    autoFocus,
}: InputSelectProps) => {
    const itemToString = (item) => {
        return item ? item.label : '';
    };

    const filterOptions = (options: FormSelectChoice[], inputValue: string): FormSelectChoice[] => {
        const filteredOptions: FormSelectChoice[] = (
            options.filter(option =>
                    !inputValue ||
                    option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                    option.value.toString().toLowerCase().includes(inputValue.toLowerCase()),
            )
        );
        return filteredOptions;
    };

    const handleOnBlur = (event: React.FormEvent<HTMLInputElement>, selectHighlightedItem: Function, selectedItem: FormSelectChoice) => {
        if ((!selectedItem) || (event.currentTarget.value !== selectedItem.label)) {
            selectHighlightedItem();
        }
    };

    const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (typeof onKeyDown === 'function') {
            onKeyDown(event);
        }
    };

    return (
        <Downshift
        onChange={onChange}
        itemToString={itemToString}
        initialSelectedItem={value && value.value && value.label ? value : undefined}
        defaultHighlightedIndex={0}>
            {({
                clearSelection,
                getInputProps,
                getItemProps,
                getMenuProps,
                getToggleButtonProps,
                highlightedIndex,
                inputValue,
                isOpen,
                openMenu,
                selectedItem,
                selectHighlightedItem,
            }) =>
            <div className={`sid-input-select-list ${className}`}>
                <input
                    {
                        ...getInputProps({
                            onFocus: () => openMenu(),
                            onBlur: event => handleOnBlur(event, selectHighlightedItem, selectedItem),
                            onKeyDown: event => handleOnKeyDown(event),
                        })
                    }
                    autoFocus={autoFocus}
                    id={inputId}
                    className={`sid-l-full-width sid-text-input ${isErrored ? 'sid-text-input--error' : ''}`}
                    disabled={disabled}
                    placeholder={ placeholder }
                    ref={input => setRef(fieldId, input)}
                />
                <InputSelectButtonComponent
                    disabled={disabled}
                    getToggleButtonProps={getToggleButtonProps}
                    isOpen={isOpen}
                    clearSelection={selectedItem ? clearSelection : undefined}
                />

                {isOpen
                    ?
                    <div>
                        {filterOptions(options, inputValue).length > 0 ?
                            <InputSelectListComponent
                                highlightedIndex={ highlightedIndex }
                                itemToString={ itemToString }
                                options={ filterOptions(options, inputValue) }
                                getItemProps={ getItemProps }
                                getMenuProps={ getMenuProps }
                            />
                            :
                            <div className={'sid-input-select-list__menu'} {...getMenuProps()}>
                                <div className={'sid-input-select-list__message'}>
                                    <FormattedHTMLMessage
                                        id="noOptions"
                                        defaultMessage="No Options"
                                    />
                                </div>
                            </div>
                        }
                    </div>
                : null}
            </div>}
        </Downshift>
    );
};


InputSelect.propTypes = {
    onChange: propTypes.func.isRequired,
    options: propTypes.array.isRequired,
    className: propTypes.string,
    disabled: propTypes.bool,
    inputId: propTypes.string,
    isErrored: propTypes.bool,
    onKeyDown: propTypes.func,
    placeholder: propTypes.string,
    value: propTypes.object,
};

InputSelect.defaultProps = {
    className: undefined,
    disabled: false,
    inputId: undefined,
    isErrored: false,
    onKeyDown: undefined,
    placeholder: '',
    value: undefined,
};

export const InputSelectComponent = InputSelect;
